import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Linking, View } from 'react-native';
import {
  Components,
  LoadingOverlay,
  Theme,
} from '@oneamerica/dxp-ui-components';
import responsiveStyle from '../../style';
import InfoWrapper from '../../../../components/InfoWrapper/InfoWrapper';
import { getPolicyTitleValue } from '../../helpers';
import { analyticsTags } from '../../../../utils/Analytics/AnalyticsTags';
import { trackCustomEvent } from '../../../../utils/Analytics/AdobeAnalyticsUtils';
import { useDispatch, useSelector } from 'react-redux';
import { getScreenType, isHomeOfficeUser } from '../../../../utils';
import { PNC_SHO_URL } from '../../../../config/getEnvironmentVars';
import MakePaymentModal from '../../../PolicyDetails/components/BillingAndPremium/MakePaymentModal';
import { useFocusEffect } from '@react-navigation/native';
import { AlertLevel } from '../../../../constants/UtilsConstants';
import BreakpointConstants from '../../../../constants/BreakpointConstants';
import LegalDisclaimerModal from '../../../PolicyDetails/components/BillingAndPremium/LegalDisclaimerModal';
import { GET_PAYMENTS_DATA } from '../../../../redux/payments/types';
import { ProcessingTroubleModal } from '../../../../components/ProcessingTroubleModal';
import { resetPaymentsEncryptedData } from '../../../../redux/payments/paymentsSlice';

const handlePaymentStatusFromURL = (
  policyNumber: string,
  apiAccountNumber: string,
  apiHasPending: boolean,
  getPolicySessionKey: (pn: string) => string,
  getHandledKey: (pn: string) => string,
  redirectShownRef: React.MutableRefObject<boolean>,
  setPaymentStatus: (status: string | undefined) => void,
) => {
  const key = getPolicySessionKey(policyNumber);
  const storedStatus = sessionStorage.getItem(key);

  const urlParams = new URLSearchParams(window.location.search);
  const initialRedirectIsSuccess = urlParams.get('paymentStatus') === 'success';

  const handledKey = getHandledKey(policyNumber);
  const handled = sessionStorage.getItem(handledKey) === 'true';

  const isApiPendingForThisPolicy =
    Boolean(apiHasPending) && apiAccountNumber === policyNumber;

  if (initialRedirectIsSuccess && !handled && policyNumber) {
    sessionStorage.setItem(key, 'success');
    sessionStorage.setItem(handledKey, 'true');
    redirectShownRef.current = true;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('paymentStatus');
      window.history.replaceState(null, '', u.toString());
    } catch {}
    setPaymentStatus('success');
    return;
  }

  if (isApiPendingForThisPolicy) {
    if (storedStatus === 'success' && redirectShownRef.current === true) {
      setPaymentStatus('success');
      return;
    }
    setPaymentStatus('pending');
    return;
  }

  if (storedStatus === 'success') setPaymentStatus('success');
  else setPaymentStatus(undefined);
};

const calculatePremiumType = (
  basePremium: number,
  paidUpAddition: number,
  premiumDeposit: number,
): string | null => {
  const premiums = [
    basePremium > 0 ? 'BASE' : null,
    paidUpAddition > 0 ? 'PUAR' : null,
    premiumDeposit > 0 ? 'PDR' : null,
  ].filter(Boolean);

  if (premiums.length === 1) {
    const premiumType = premiums[0];
    if (premiumType === 'BASE') return 'P';
    if (premiumType === 'PUAR') return 'U';
  }
  return null;
};

export const BillingAndPremiumCard = ({
  className,
  initialPolicyData,
  generalPolicyData,
  linkStyle,
  moreBillingAndPremium,
  navigateToPolicyDetails,
  row,
  account,
}) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const dispatch = useDispatch();

  const { width } = useContext(Theme.ResponsiveContext);
  const style = responsiveStyle();
  const screenType = getScreenType(width);
  const isMobile = screenType === 'SM';
  const phnLinkStyle = {
    style: style.phnlinkStyle,
    linkStyle: style.phnlinkStyle,
    linkHoverStyle: style.phnlinkStyle,
    hoverStyle: style.phnlinkStyle,
    focusStyle: style.phnlinkStyle,
  };

  const { ...policyData } = useSelector((state: any) => state.policies);
  const allowBillingCard =
    policyData.getPoliciesListData?.data?.policySummary?.policyHighlights[0]
      ?.accountConfig?.allowBillingCard;
  const hasPendingPayment =
    policyData.getPoliciesListData?.data?.policySummary?.policyHighlights[0]
      ?.accountConfig?.hasPendingPayment;

  const [isPendingBannerVisible, setIsPendingBannerVisible] = useState(true);
  const paymentEncryptedData = useSelector(
    (state: any) => state.paymentEncryptedData,
  );

  const formRef = useRef<HTMLFormElement | null>(null);
  const getPolicySessionKey = (policyNumber) => `paymentStatus_${policyNumber}`;
  const getHandledKey = (policyNumber) =>
    `paymentStatusHandled_${policyNumber}`;

  const redirectShownRef = useRef(false);

  useEffect(() => {
    const policyNumber = account?.number;
    const apiAccountNumber =
      policyData?.getPoliciesListData?.data?.policySummary?.account?.number;
    const apiHasPending =
      policyData?.getPoliciesListData?.data?.policySummary
        ?.policyHighlights?.[0]?.accountConfig?.hasPendingPayment;

    handlePaymentStatusFromURL(
      policyNumber,
      apiAccountNumber,
      apiHasPending,
      getPolicySessionKey,
      getHandledKey,
      redirectShownRef,
      setPaymentStatus,
    );
  }, [account?.number, policyData]);

  useFocusEffect(
    React.useCallback(() => {
      setIsPendingBannerVisible(true);
    }, []),
  );

  const shouldShowPendingBanner =
    hasPendingPayment === true &&
    isPendingBannerVisible &&
    paymentStatus !== 'success';

  const shouldShowSuccessBanner = paymentStatus === 'success';

  const showMakePaymentButton = useMemo(() => {
    return (
      policyData.getPoliciesListData?.data?.policySummary?.policyHighlights[0]
        ?.accountConfig?.allowPaymentButton === true
    );
  }, [policyData]);

  const isHomeOfcUser = isHomeOfficeUser();

  const basePremium = Number.parseFloat(generalPolicyData?.billingAmount || 0);
  const paidUpAddition = Number.parseFloat(
    generalPolicyData?.billRiderAmt || 0,
  );
  const premiumDeposit = Number.parseFloat('0');

  const shouldDisablePaymentButton =
    (basePremium <= 0 && paidUpAddition <= 0 && premiumDeposit <= 0) ||
    paymentStatus === 'success';

  useEffect(() => {
    dispatch(resetPaymentsEncryptedData());
    setPaymentData(null);
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    if (paymentEncryptedData?.data || paymentEncryptedData?.error) {
      setIsLoading(false);
    }
    if (paymentEncryptedData?.data) {
      setPaymentData(paymentEncryptedData.data);
    } else if (paymentEncryptedData?.error) {
      trackCustomEvent(analyticsTags.CapturePaymentDataApiFailureHomePage);
      console.error('Error fetching payment data:', paymentEncryptedData.error);
      setPaymentData(null);
    }
  }, [paymentEncryptedData]);

  const onPressMakeAPayment = () => {
    setIsLoading(true);
    trackCustomEvent(analyticsTags.CaptureMakeAPaymentButtonHomeTags);

    const queryParam = calculatePremiumType(
      basePremium,
      paidUpAddition,
      premiumDeposit,
    );

    if (queryParam) {
      dispatch({
        type: GET_PAYMENTS_DATA,
        payload: {
          token: policyData?.token,
          policyNumber:
            policyData?.getPoliciesListData?.data?.policySummary?.account
              ?.number,
          paymentTypes: queryParam,
          isLoading: true,
        },
      });
    } else {
      setModalVisible(true);
      setIsLoading(false);
    }
  };

  const handleLegalDisclaimerAgree = () => {
    if (sessionStorage.getItem('pncCallbackRole')) {
      sessionStorage.removeItem('pncCallbackRole');
    }
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const getPaymentTypes = () => {
    const generalPolicyData =
      policyData?.getPoliciesListData?.data?.policySummary
        ?.policyHighlights?.[0]?.generalPolicyData;
    const payments: { title: string; amount: number }[] = [];

    if (generalPolicyData?.billingAmount) {
      payments.push({
        title: 'Base Premium',
        amount: generalPolicyData?.billingAmount,
      });
    } else {
      payments.push({
        title: 'Base Premium',
        amount: 0,
      });
    }

    if (generalPolicyData?.billRiderAmt) {
      payments.push({
        title: 'Paid Up Addition',
        amount: generalPolicyData?.billRiderAmt,
      });
    } else {
      payments.push({
        title: 'Paid Up Addition',
        amount: 0,
      });
    }

    const filteredPayments = payments.filter((item) => item.amount > 0);
    return filteredPayments;
  };

  const sendAnalytics = () => {
    trackCustomEvent(analyticsTags.CaptureMakeAPaymentCloseButtonTags);
  };

  const getSelectedPaymentsCodes = (selectedPayments): string[] => {
    const hasBase = selectedPayments.some(
      (p) => p.title?.toLowerCase() === 'base premium',
    );
    const hasPuar = selectedPayments.some(
      (p) => p.title?.toLowerCase() === 'paid up addition',
    );
    return [hasBase && 'P', hasPuar && 'U'].filter(Boolean) as string[];
  };

  return initialPolicyData.length > 0 && allowBillingCard ? (
    <>
      <LoadingOverlay loading={isLoading} />
      <View className={className}>
        <View style={[style.cardShadow, row > 1 && style.flex]}>
          <View
            style={[
              style.cardHeader,
              !isMobile
                ? style.billingAndPremiumTitle
                : style.billingAndPremiumTitleSM,
            ]}
          >
            <Components.Text.H3 style={[style.cardHeaderTitle]}>
              Billing & Premiums
            </Components.Text.H3>
            {showMakePaymentButton && (
              <>
                <View style={{ alignSelf: 'flex-end' }}>
                  <Components.Button
                    className="mx-2"
                    accessibilityRole="button"
                    id="btnOutline"
                    testID="makeAPaymentBtn"
                    theme="primary"
                    disabled={
                      isHomeOfcUser ||
                      shouldDisablePaymentButton ||
                      hasPendingPayment ||
                      isLoading
                    }
                    onPress={() => {
                      if (isHomeOfcUser || shouldDisablePaymentButton) return;
                      onPressMakeAPayment();
                    }}
                    style={[style.makeaPaymentButton]}
                  >
                    Make this Payment
                  </Components.Button>
                </View>
                <form
                  ref={formRef}
                  action={PNC_SHO_URL}
                  method="post"
                  style={{ display: 'none' }}
                >
                  <input
                    type="hidden"
                    name="BillTypeLabel"
                    value={paymentData?.billTypeLabel ?? ''}
                  />
                  <input
                    type="hidden"
                    name="CompanyName"
                    value={paymentData?.companyName ?? ''}
                  />
                  <input
                    type="hidden"
                    name="AmountDueLabel"
                    value={paymentData?.amountDueLabel ?? ''}
                  />
                  <input
                    type="hidden"
                    name="EmailAddress"
                    value={paymentData?.emailAddress ?? ''}
                  />
                  <input
                    type="hidden"
                    name="FDICode"
                    value={paymentData?.fdiCode ?? ''}
                  />
                  <input
                    type="hidden"
                    name="PolicyNumber"
                    value={paymentData?.policyNumber ?? ''}
                  />
                  <input
                    type="hidden"
                    name="ClientID"
                    value={paymentData?.clientID ?? ''}
                  />
                  <input
                    type="hidden"
                    name="ID"
                    value={paymentData?.ID ?? ''}
                  />
                </form>
              </>
            )}
          </View>
          {shouldShowPendingBanner ? (
            <Components.AlertMessage
              className="mt-4"
              showCloseButton={true}
              onCloseMessage={() => {
                setIsPendingBannerVisible(false);
              }}
              alertLevel={AlertLevel.ALERT}
              iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
              title="There is a pending transaction in process"
            >
              <Components.Text style={style.alertBannerContentMargin}>
                You cannot make any changes until it is complete
              </Components.Text>
            </Components.AlertMessage>
          ) : (
            shouldShowSuccessBanner && (
              <Components.AlertMessage
                className="mt-4"
                showCloseButton={true}
                alertLevel={AlertLevel.SUCCESS}
                iconSize={BreakpointConstants.isLG(width) ? 52.36 : 24}
                title="Thank you for your payment"
              >
                <Components.Text style={style.marginTop16}>
                  {`Call us at `}
                  <Components.Link
                    onPress={() => {
                      Linking.openURL('tel:800-537-6442');
                    }}
                    accessibilityLabel="contact number"
                    accessibilityRole="button"
                    {...phnLinkStyle}
                  >
                    800-537-6442
                  </Components.Link>
                  {` to make any changes`}
                </Components.Text>
              </Components.AlertMessage>
            )
          )}

          <View className="mt-4" style={style.horizontalDivider} />
          <View style={style.contentWrapper}>
            {!isMobile ? (
              <View className="pt-2">
                <View className="flex-row flex-wrap">
                  {initialPolicyData.map((item, index) => {
                    return (
                      <View key={`${item}_${index}`} className="w-1/3">
                        <InfoWrapper
                          title={
                            getPolicyTitleValue(generalPolicyData, item)
                              .title || ''
                          }
                          information={
                            getPolicyTitleValue(generalPolicyData, item)
                              .value || ''
                          }
                          boldInfo={index === 0}
                          subTitle={index > 0}
                          boldInfoStyle={{ textAlign: 'left', fontSize: 24 }}
                        />
                      </View>
                    );
                  })}
                </View>
              </View>
            ) : (
              <View className="py-2" style={style.row}>
                <View className="items-center" style={style.flex}>
                  {initialPolicyData.map((item, index) => {
                    return (
                      <View key={`${item}_${index}`}>
                        <InfoWrapper
                          title={
                            getPolicyTitleValue(generalPolicyData, item)
                              .title || ''
                          }
                          information={
                            getPolicyTitleValue(generalPolicyData, item)
                              .value || ''
                          }
                          boldInfo={index === 0}
                          subTitle={index > 0}
                          centerAlign
                        />
                      </View>
                    );
                  })}
                </View>
              </View>
            )}
          </View>
          {moreBillingAndPremium.length > 3 && (
            <Components.Link
              accessibilityLabel="View Billing and Premium Details"
              accessibilityRole="button"
              onPress={() => {
                trackCustomEvent(
                  analyticsTags.CaptureBillingAndPremiumsCardViewDetailsTags,
                );
                navigateToPolicyDetails('BillingandPremium');
              }}
              {...linkStyle}
            >
              View Details
            </Components.Link>
          )}
        </View>
      </View>

      <MakePaymentModal
        onSubmit={(selectedPayments) => {
          setIsLoading(true);
          trackCustomEvent(analyticsTags.CaptureMakeAPaymentSubmitButtonTags);
          dispatch({
            type: GET_PAYMENTS_DATA,
            payload: {
              token: policyData?.token,
              policyNumber:
                policyData?.getPoliciesListData?.data?.policySummary?.account
                  ?.number,
              paymentTypes: getSelectedPaymentsCodes(selectedPayments),
              isLoading: true,
            },
          });
          setModalVisible(false);
        }}
        onCloseAnalytics={sendAnalytics}
        isModalVisible={isModalVisible}
        setModalVisible={setModalVisible}
        payments={getPaymentTypes()}
      />
      <LegalDisclaimerModal
        isVisible={!!paymentEncryptedData?.data}
        onAgree={handleLegalDisclaimerAgree}
        isHomePage={true}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setPaymentData(null);
          setIsLoading(false);
        }}
      />
      <ProcessingTroubleModal
        showModal={!!paymentEncryptedData?.error}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setIsLoading(false);
          trackCustomEvent(
            analyticsTags.CaptureProcessingTroubleModalCloseHomePage,
          );
        }}
      />
    </>
  ) : null;
};
